import React, { useState, useEffect, useRef } from 'react';
import { db, storage } from '../firebase';
import { doc, getDoc, updateDoc, collection, getDocs, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../AuthProvider';
import { useNavigate, useParams } from 'react-router-dom';
import PageWrapperAuto from '../components/PageWrapperAuto';
import { useUI } from '../UIContext';
import { useUserPrefs } from '../UserPrefsProvider';
import { fetchTunearchPage } from '../utils/fetchTunearchPage';
import AbcImportPreview from '../components/AbcImportPreview';
// import { usePrompt } from '../hooks/usePrompt';
import { setDirtyFlag } from '../safeNavigate';
import KeyInput from '../components/KeyInput'; // Adjust path if needed
import ABCJS from 'abcjs';
import AbcNotation from '../components/AbcNotation'; // Add this if not already
import ConfirmLeaveModal from '../components/ConfirmLeaveModal';
import { keyRoot, normalizeTagsArray, normalizeTag } from '../utils/normalize';
import { normalizeKey, normalizeType } from '../utils/normalize';
import { sanitizeAbcBody } from '../components/sanitizeAbcBody';
import { cleanAndFillAbc } from '../utils/cleanAndFillAbc';
import {
  ref,
  uploadBytes,
  deleteObject,
  getDownloadURL,
  listAll
} from 'firebase/storage';
import CustomTagInput from '../components/CustomTagInput';
import { loadTuneHybrid } from '../utils/loadTuneHybrid'; // You’ll create this
import dexie from '../offlineDB';
import { flushPendingWrites } from '../utils/flushPendingWrites';

export default function EditTunePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
const { inputStyle, textStyle, cardStyle } = useUI();

const [abcWarnings, setAbcWarnings] = useState({
  missingKey: false,
  missingMeter: false,
  missingLength: false,
  hasX: false,
  hasEmptyHeaders: false
});

  const userPrefs = useUserPrefs();

  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importedTune, setImportedTune] = useState(null);
  const [importFields, setImportFields] = useState({ title: true, type: true, key: true, abc: true });
  const [tunearchUrl, setTunearchUrl] = useState('');
  const [customAbcOverride, setCustomAbcOverride] = useState('');
  const [originalAbc, setOriginalAbc] = useState('');
  const textareaRef = useRef(null);
  const lyricsRef = useRef(null);
  const chordsRef = useRef(null);
  const notesRef = useRef(null);

  const abcRenderRef = useRef(null);
  const [showAbcPreview, setShowAbcPreview] = useState(false);
  const [sessionSearchAttempted, setSessionSearchAttempted] = useState(false);
  const [sessionSuggestions, setSessionSuggestions] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [pendingLeaveAction, setPendingLeaveAction] = useState(null);

  const [focused, setFocused] = useState(false);
  const [tagSuggestions, setTagSuggestions] = useState([]);


// usePrompt('You have unsaved changes. Are you sure you want to leave?', isDirty);


const adjustHeight = (ref) => {
  if (ref?.current) {
    ref.current.style.height = 'auto';
    ref.current.style.height = ref.current.scrollHeight + 'px';
  }
};




  const openTunearchSearch = () => {
    const title = formData?.title?.trim();
    if (!title) return;
    const searchUrl = `https://tunearch.org/wiki/Special:Search?search=${encodeURIComponent(title)}`;
    window.open(searchUrl, '_blank');
  };

  const handlePasteTunearchUrl = async (url) => {
    if (!url.includes('tunearch.org/wiki/')) {
      alert('Please paste a valid Tunearch link.');
      return;
    }
    try {
      const result = await fetchTunearchPage(url);
      if (result) {
        setImportedTune({
          title: result.title || '',
          key: result.key || '',
          abc: result.abc || '',
          composer: result.author || '',
          link1: result.sourceUrl || url
        });
        setImportFields({ title: true, key: true, abc: true, composer: true, link1: true });
      } else {
        alert('Could not parse tune from this page.');
      }
    } catch (err) {
      console.error('Error loading pasted Tunearch page:', err);
      alert('Error importing from Tunearch page.');
    }
  };



useEffect(() => {
  setDirtyFlag(isDirty); // keep the global flag in sync
}, [isDirty]);

  
useEffect(() => {
  return () => setDirtyFlag(false); // 🧹 Clear when leaving the page
}, []);

  const openLyricsSearch = () => {
    const title = formData?.title?.trim();
    if (!title) return;
    const query = encodeURIComponent(title);
    const url = `https://www.bluegrasslyrics.com/?s=${query}`;
    window.open(url, '_blank');
  };

  const ratingOptions = ['—', '★', '★★', '★★★', '★★★★', '★★★★★'];
  const genreOptions = userPrefs?.prefs?.customGenres || ['Irish', 'Old Time', 'Bluegrass', 'Klezmer'];
  const typeOptions = userPrefs?.prefs?.customTypes || ['Reel', 'Jig', 'Hornpipe', 'Polka', 'Waltz', 'Tune', 'Song'];
  const statusOptions = ['', 'Need to Learn', 'Learning', 'Learned'];
  const [sessionSearchLoading, setSessionSearchLoading] = useState(false);

function suggestMeterForType(type) {
  const t = type?.toLowerCase();
  if (!t) return '';

  if (t.includes('slip jig') || t.includes('hop jig')) return '9/8';
  if (t.includes('jig')) return '6/8';
  if (t.includes('slide')) return '12/8';
  if (t.includes('reel') || t.includes('hornpipe')) return '4/4';
  if (t.includes('waltz') || t.includes('mazurka')) return '3/4';
  if (t.includes('polka')) return '2/4';
  if (t.includes('barndance') || t.includes('barn dance') || t.includes('march')) return '4/4';

  return '';
}

function cleanAbcHeadersAndLines(abc) {
  const lines = abc.split('\n');

  const cleaned = lines
    .map(line => line.trim())
    .filter(line => {
      if (line === '') return false;
      if (/^X:\s*\S*/i.test(line)) return false;
      if (/^[A-Z]:\s*$/i.test(line)) {
        // Allow empty L: through so it can be detected, but still clean it
        return !/^L:/i.test(line);
      }
      return true;
    });

  return cleaned.join('\n');
}

  
 useEffect(() => {
  if (!user || !id) return;

  const load = async () => {
    try {
      if (navigator.onLine) {
        await flushPendingWrites(user); // ✅ ensure any offline edits are pushed first
      }

      const data = await loadTuneHybrid(user, id);
      if (data) {
        setFormData(data);

        // ✅ ABC validation block
        if (data.abc) {
          const abc = data.abc;
          const lines = abc.split('\n');

          const hasKey = /^K:\s*\S+/m.test(abc);
          const hasMeter = /^M:\s*\S+/m.test(abc);
          const hasLength = /^L:\s*\S+/m.test(abc);
          const hasX = /^X:\s*\S*/m.test(abc);
          const hasEmptyHeaders = lines.some(line => /^[A-Z]:\s*$/m.test(line) && !/^L:/m.test(line));

          setAbcWarnings({
            missingKey: !hasKey,
            missingMeter: !hasMeter,
            missingLength: !hasLength,
            hasX,
            hasEmptyHeaders
          });
        }
      } else {
        setFormData(null);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading tune (hybrid):', err);
      setLoading(false);
    }
  };

  load();
}, [user, id]);


  useEffect(() => {
  if (!user) return;

  const fetchTags = async () => {
    try {
      const snap = await getDocs(collection(db, 'users', user.uid, 'tunes'));
      const allTags = new Set();
      snap.forEach(doc => {
        const data = doc.data();
        if (Array.isArray(data.customTags)) {
          data.customTags.forEach(tag => allTags.add(tag));
        }
      });
      setTagSuggestions([...allTags]);
    } catch (err) {
      console.error("Error loading custom tag suggestions:", err);
    }
  };

  fetchTags();
}, [user]);

useEffect(() => {
  if (!user || !id) return;

  const path = `users/${user.uid}/tunes/${id}`;
  const storageRef = ref(storage, path);

  listAll(storageRef)
    .then(res => Promise.all(res.items.map(fileRef =>
   getDownloadURL(fileRef).then(url => ({
  name: fileRef.name,
  url,
  path: fileRef.fullPath,
  type: fileRef.name.endsWith('.pdf')
    ? 'application/pdf'
    : fileRef.name.match(/\.(jpg|jpeg|png|gif)$/i)
    ? 'image/jpeg'
    : 'application/octet-stream'
}))

    )))
    .then(setExistingFiles)
    .catch(err => console.error("Error loading existing files:", err));
}, [user, id]);


useEffect(() => {
  if (textareaRef.current) {
    textareaRef.current.style.height = 'auto';
    textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
  }
}, [customAbcOverride]);

useEffect(() => {
  if (textareaRef.current && formData?.abc) {
    textareaRef.current.style.height = 'auto';
    textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
  }
}, [formData?.abc]);

useEffect(() => {
  adjustHeight(lyricsRef);
}, [formData?.lyrics]);

useEffect(() => {
  adjustHeight(chordsRef);
}, [formData?.chords]);

useEffect(() => {
  adjustHeight(notesRef);
}, [formData?.notes]);


useEffect(() => {
  if (showAbcPreview && abcRenderRef.current && formData?.abc?.trim()) {
    ABCJS.renderAbc(abcRenderRef.current, formData.abc, {
      responsive: 'resize',
      add_classes: true
    });
  }
}, [showAbcPreview, formData?.abc]);


useEffect(() => {
  const handleBeforeUnload = (e) => {
    if (isDirty) {
      e.preventDefault();
      e.returnValue = ''; // Required for Chrome to show confirmation dialog
    }
  };

  window.addEventListener('beforeunload', handleBeforeUnload);
  return () => window.removeEventListener('beforeunload', handleBeforeUnload);
}, [isDirty]);


useEffect(() => {
  if (importedTune?.versions?.length) {
    const raw = importedTune.versions[importedTune.selectedIndex]?.abc || '';
    const cleaned = cleanAndFillAbc({
      rawAbc: raw,
      type: normalizeType(importedTune.type),
      key: normalizeKey(importedTune.key)
    });
    setCustomAbcOverride(cleaned);
    setOriginalAbc(cleaned);
  }
}, [importedTune?.selectedIndex]);

const insertSuggestedHeaders = () => {
  const abc = formData.abc || '';
  const lines = abc.split('\n');

  let hasKey = false;
  let hasMeter = false;
  let hasLength = false;

  const newLines = [];
  for (let line of lines) {
    const trimmed = line.trim();
    if (/^K:\s*\S+/i.test(trimmed)) hasKey = true;
    if (/^M:\s*\S+/i.test(trimmed)) hasMeter = true;
    if (/^L:\s*\S+/i.test(trimmed)) hasLength = true;
    newLines.push(line);
  }

  const insertIndex = newLines.findIndex(line => /^R:/i.test(line)) + 1 || 1;
  const inserts = [];

  const meter = suggestMeterForType(formData.type);
  const needsMeter = !hasMeter && meter;
  const needsKey = !hasKey && formData.key;

  if (needsMeter) inserts.push(`M:${meter}`);
  if (!hasLength && ['2/4', '4/4', '3/4', '6/8', '9/8', '12/8'].includes(meter)) {
  inserts.push('L:1/8');
}

  if (needsKey) inserts.push(`K:${formData.key}`);

  if (inserts.length > 0) {
    newLines.splice(insertIndex, 0, ...inserts);
    const updatedAbc = newLines.join('\n');
    setFormData(prev => ({ ...prev, abc: updatedAbc }));
    setIsDirty(true);

    // Recompute warnings
    const hasK = /^K:\s*\S+/m.test(updatedAbc);
    const hasM = /^M:\s*\S+/m.test(updatedAbc);
    const hasX = /^X:\s*\S*/m.test(updatedAbc);
    const hasEmptyHeaders = updatedAbc.split('\n').some(line => /^[A-Z]:\s*$/m.test(line));

    setAbcWarnings({
      missingKey: !hasK,
      missingMeter: !hasM,
      hasX,
      hasEmptyHeaders
    });
  }
};




const handleChange = (e) => {
  const { name, value } = e.target;
  setFormData(prev => ({ ...prev, [name]: value }));
  setIsDirty(true);

  if (name === 'abc') {
    const abc = value || '';
    const lines = abc.split('\n');

    const hasKey = /^K:\s*\S+/m.test(abc);
    const hasMeter = /^M:\s*\S+/m.test(abc);
    const hasLength = /^L:\s*\S+/m.test(abc);
    const hasX = /^X:\s*\S*/m.test(abc);
    const hasEmptyHeaders = lines.some(line => /^[A-Z]:\s*$/m.test(line) && !/^L:/m.test(line));

    setAbcWarnings({
      missingKey: !hasKey,
      missingMeter: !hasMeter,
      missingLength: !hasLength,
      hasX,
      hasEmptyHeaders
    });


  }

};

const [isSaving, setIsSaving] = useState(false); // 🆕 debounce state

const handleSave = async () => {
  if (!user || !formData || isSaving) return;
  setIsSaving(true); // 🧯 prevent double-click

  const updatedData = {
    ...formData,
    type: formData.type ? normalizeType(formData.type) : '',
    abc: formData.abc || '',
    customTags: Array.isArray(formData.customTags)
      ? normalizeTagsArray(formData.customTags)
      : [],
    attachments: existingFiles.map(file => ({
      name: file.name,
      url: file.url,
      type: file.type || '',
      path: file.path || ''
    })),
    updatedAt: new Date().toISOString(),
  };

  const ref = doc(db, 'users', user.uid, 'tunes', id);

  try {
    // 🔁 Save prefs tags
    const prefsRef = doc(db, 'users', user.uid, 'preferences', 'settings');
    const prefsSnap = await getDoc(prefsRef);
    const prefsData = prefsSnap.exists() ? prefsSnap.data() : {};
    const existingPrefsTags = Array.isArray(prefsData.customTags) ? prefsData.customTags : [];
    const mergedTags = Array.from(new Set([
      ...normalizeTagsArray(updatedData.customTags || []),
      ...existingPrefsTags.map(normalizeTag)
    ]));

if (navigator.onLine) {
  await updateDoc(prefsRef, { customTags: mergedTags });
} else {
  await dexie.pendingWrites.put({
    type: 'update',
    collection: 'preferences',
    docId: 'settings',
    data: { customTags: mergedTags }
  });
}
  } catch (err) {
    console.warn('Could not sync tags to user preferences:', err);
  }

  try {
    if (navigator.onLine) {
      await updateDoc(ref, updatedData);
      await dexie.tunes.put({ id, ...updatedData });
    } else {
      await dexie.tunes.put({ id, ...updatedData });
      await dexie.pendingWrites.put({
        id: `${id}__update`,
        type: 'tune',             // ✅ type = tune
        operation: 'update',      // ✅ operation = update
        collection: 'tunes',
        docId: id,
        data: { id, ...updatedData },
      });

    }

    setIsDirty(false);
    navigate(`/tune/${id}`);

    // ✅ Replace alert with non-blocking notification
    const msg = navigator.onLine
      ? '✅ Tune saved.'
      : '💾 Saved offline. Will sync when online.';
    console.log(msg); // Replace this line with toast(msg) if you use a toast library

  } catch (err) {
    console.error('Error saving tune:', err);
    alert(`❌ Failed to save tune: ${err.message || 'Unknown error'}`);
  } finally {
    setIsSaving(false); // 🔓 re-enable Save
  }
};





  const fetchSessionSuggestions = () => {
    const title = formData?.title?.trim();
    setSessionSearchAttempted(true);
    setSessionSearchLoading(true); // ✅ START loading

    if (!title || title.length < 3) {
      setSessionSuggestions([]);
      setSessionSearchLoading(false); // ✅ STOP loading early
      return;
    }

    fetch(`https://thesession.org/tunes/search?q=${encodeURIComponent(title)}&format=json`)
      .then(res => res.json())
      .then(json => {
        const matches = Array.isArray(json.tunes)
          ? json.tunes.filter(t => t.name.toLowerCase().includes(title.toLowerCase())).slice(0, 5)
          : [];
        setSessionSuggestions(matches);
      })
      .catch(err => {
        console.error('Suggestion error:', err);
        setSessionSuggestions([]);
      })
      .finally(() => {
        setSessionSearchLoading(false); // ✅ STOP loading when done
      });
  };


const [newFiles, setNewFiles] = useState([]);
const [existingFiles, setExistingFiles] = useState([]);
const fileInputRef = useRef(null);

  

  const importFromSession = async (tuneId) => {
    try {
      const res = await fetch(`https://thesession.org/tunes/${tuneId}?format=json`);
      const data = await res.json();
      const setting = data.settings?.[0] || {};

      setImportedTune({
        title: data.name || '',
        type: data.type || '',
        key: setting.key || '',
        abc: setting.abc || '',
        versions: data.settings.map((setting, index) => {
          const abcLines = [
            'X:1',
            `T:${data.name || ''}`,
            `M:${setting.meter || ''}`,
            `L:${setting.unit_note_length || ''}`,
            `R:${data.type || ''}`,
            `Q:${setting.tempo || ''}`,
            `Z:${data.transcribed_by || ''}`,
            `S:${data.source || ''}`,
            `N:${data.note || ''}`,
            `K:${setting.key || ''}`,
            setting.abc || ''
          ];
          return {
            index,
            abc: abcLines.join('\n')
          };
        }),

        selectedIndex: 0
      });


      setImportFields({ title: true, type: true, key: true, abc: true });
    } catch (err) {
      console.error(err);
      alert('Error importing tune');
    }
  };

  const applyImportedFields = () => {
  if (!formData || !importedTune) return;
  const updated = { ...formData };

  for (const field in importFields) {
    if (!importFields[field]) continue; // ✅ skip unchecked fields

    if (field === 'abc') {
      const version = importedTune.versions?.[importedTune.selectedIndex];
      const fullAbc = version?.abc || '';

const cleaned = cleanAndFillAbc({
  rawAbc: fullAbc,
  type: importedTune.type,
  key: importedTune.key
});
updated.abc = cleaned;

    } else {
      updated[field] = importedTune[field] || '';
    }
  }

  setFormData(updated);
  setImportedTune(null);
};



const keyInputValue = formData?.key || '';
const root = keyRoot(keyInputValue);
const canon = [
  `${root} major`,
  `${root} minor`,
  `${root} dorian`,
  `${root} mixolydian`
];
const keySuggestions = canon;


const handleFileChange = async (e) => {
  const files = Array.from(e.target.files || []);
  const uploads = [];

  for (const file of files) {
    const storageRef = ref(storage, `users/${user.uid}/tunes/${id}/${file.name}`);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);

    uploads.push({
      name: file.name,
      url,
      path: storageRef.fullPath,
      type: file.type
    });

  }

  setExistingFiles(prev => [...prev, ...uploads]);
  setNewFiles([]);
  if (fileInputRef.current) fileInputRef.current.value = null;
};

const handleDeleteFile = async (file) => {
  if (!window.confirm(`Are you sure you want to delete ${file.name}?`)) return;

  try {
    await deleteObject(ref(storage, file.path));
    setExistingFiles(prev => prev.filter(f => f.path !== file.path));
  } catch (err) {
    console.error("Delete failed", err);
    alert("Failed to delete file");
  }
};



  if (!user) return <PageWrapperAuto>Please log in to edit tunes.</PageWrapperAuto>;
  if (loading) return <PageWrapperAuto>Loading tune...</PageWrapperAuto>;
  if (!formData) return <PageWrapperAuto>Tune not found.</PageWrapperAuto>;



  return (
    <PageWrapperAuto>


  <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginBottom: '10px', flexWrap: 'wrap' }}>

      <button
        className="big-button"
        onClick={() => {
          if (isDirty) {
            setPendingLeaveAction(() => () => navigate(-1));
            setShowLeaveModal(true);
          } else {
            navigate(-1);
          }
        }}
      >
        ← Back
      </button>

  <button className="big-button" onClick={handleSave}
 
    style={{
      background: '#ccc',
      color: '#000',
      padding: '10px 20px',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '1rem'
    }}
     >💾 Save Changes</button>

</div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '600px', margin: '0 auto', marginTop: '12px' }}>
        <input style={inputStyle} name="title" placeholder="Title" value={formData.title || ''} onChange={handleChange} />


<div style={{
  display: 'flex',
  justifyContent: 'center',
  gap: '12px',
  marginTop: '6px',
  flexWrap: 'wrap',
  alignItems: 'baseline'
}}>

  <label style={{ ...textStyle, minWidth: '140px' }}>
  
    <select
      style={inputStyle}
      name="genre"
      value={formData.genre || ''}
      onChange={handleChange}
    >
      {genreOptions.map((opt, i) => (
        <option key={i} value={opt}>{opt}</option>
      ))}
    </select>
  </label>

  <label style={{ ...textStyle, minWidth: '140px' }}>
    <select
      style={inputStyle}
      name="type"
      value={formData.type || ''}
      onChange={handleChange}
    >
      {typeOptions.map((opt, i) => (
        <option key={i} value={opt}>{opt}</option>
      ))}
    </select>
  </label>

  <label style={{ ...textStyle, minWidth: '140px' }}>
<KeyInput
  value={formData.key || ''}
  onChange={handleChange}
  suggestions={keySuggestions}
  inputStyle={inputStyle}
  textStyle={textStyle}
/>
</label>

</div>



{/* Status + Rating + Label */}
<div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '10px', flexWrap: 'wrap' }}>
  <label style={{ ...textStyle, minWidth: '140px' }}>
    <select
      style={inputStyle}
      name="status"
      value={formData.status || ''}
      onChange={handleChange}
    >
      {statusOptions.map((opt, i) => (
<option key={i} value={opt}>
  {opt === '' || opt === '—' ? 'Status (—)' : opt}
</option>

      ))}
    </select>
  </label>

  <label style={{ ...textStyle, minWidth: '140px' }}>
    <select
      style={inputStyle}
      name="rating"
      value={formData.rating || '—'}
      onChange={handleChange}
    >
      {ratingOptions.map((opt, i) => (
    <option key={i} value={opt}>
  {opt === '' || opt === '—' ? 'Rating (—)' : opt}
</option>

      ))}
    </select>
  </label>

</div>
<div style={{ 
  display: 'flex', 
  justifyContent: 'center', 
  marginBottom: '10px' 
}}>
  <div style={{ width: '60%' }}>    
    <CustomTagInput
      tags={formData.customTags || []}
      setTags={(tags) => {
        setFormData(prev => ({ ...prev, customTags: tags }));
        setIsDirty(true);
      }}
      suggestions={tagSuggestions}
      inputStyle={inputStyle}
    />
  </div>






</div>


        <input style={inputStyle} name="composer" placeholder="Composer" value={formData.composer || ''} onChange={handleChange} />
        

        <textarea
          ref={textareaRef}
          name="abc"
          placeholder="ABC notation"
          value={formData.abc || ''}
          onChange={handleChange}
          style={{
            ...inputStyle,
            resize: 'vertical',
            overflowX: 'hidden',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontFamily: 'monospace'
          }}
        />

{(abcWarnings.missingKey || abcWarnings.missingMeter || abcWarnings.missingLength || abcWarnings.hasX || abcWarnings.hasEmptyHeaders) && (
  <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', padding: '10px', borderRadius: '6px', marginTop: '10px', color: '#b36b00' }}>
    <strong>Tip:</strong> Your ABC may need cleanup:
      <div style={{ marginTop: '6px', paddingLeft: '1.2em' }}>
        {abcWarnings.missingKey && <div>Missing <code>K:</code> (key)</div>}
        {abcWarnings.missingMeter && <div>Missing <code>M:</code> (meter)</div>}
        {abcWarnings.missingLength && <div>Missing <code>L:</code> (unit note length)</div>}
        {abcWarnings.hasX && <div>Includes unnecessary <code>X:</code> line</div>}
        {abcWarnings.hasEmptyHeaders && <div>Contains empty fields like <code>L:</code>, <code>Q:</code>, or <code>Z:</code></div>}
      </div>


{(abcWarnings.missingKey || abcWarnings.missingMeter || abcWarnings.missingLength) && (
  <>
    <br/>Suggested insertions:
    <pre style={{ background: '#f9f9f9', padding: '6px', borderRadius: '4px', marginTop: '6px' }}>
      {abcWarnings.missingKey ? `K:${formData.key || 'D'}\n` : ''}
      {abcWarnings.missingMeter ? `M:${suggestMeterForType(formData.type) || '4/4'}\n` : ''}
      {abcWarnings.missingLength ? 'L:1/8' : ''}
    </pre>
  </>
)}



<div style={{ marginTop: '12px', display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
      {(abcWarnings.missingKey || abcWarnings.missingMeter || abcWarnings.missingLength) && (
          <button
            onClick={insertSuggestedHeaders}
            style={{
              padding: '6px 10px',
              background: '#ffd666',
              border: 'none',
              borderRadius: '4px',
            }}
          >
            Insert Suggested Lines
          </button>
        )}

      {(abcWarnings.hasX || abcWarnings.hasEmptyHeaders) && (
        <button
          onClick={() => {
            const cleaned = cleanAbcHeadersAndLines(formData.abc || '');
            const hasKey = /^K:\s*\S+/m.test(cleaned);
            const hasMeter = /^M:\s*\S+/m.test(cleaned);
            const hasLength = /^L:\s*\S+/m.test(cleaned);
            const hasX = /^X:\s*\S*/m.test(cleaned);
            const hasEmptyHeaders = cleaned.split('\n').some(line =>
              /^[A-Z]:\s*$/m.test(line) && !/^L:/m.test(line)
            );



            setFormData(prev => ({ ...prev, abc: cleaned }));
            setIsDirty(true);

            setAbcWarnings({
              missingKey: !hasKey,
              missingMeter: !hasMeter,
              missingLength: !hasLength,
              hasX,
              hasEmptyHeaders
            });
          }}
          disabled={!abcWarnings.hasX && !abcWarnings.hasEmptyHeaders}
          style={{
            padding: '6px 10px',
            background: '#ffe0b3',
            border: 'none',
            borderRadius: '4px',
            cursor: abcWarnings.hasX || abcWarnings.hasEmptyHeaders ? 'pointer' : 'not-allowed',
            opacity: abcWarnings.hasX || abcWarnings.hasEmptyHeaders ? 1 : 0.5
          }}
        >
          Clean ABC (delete unnecessary lines)
        </button>

      )}
    </div>
  </div>
)}





        <div style={{ textAlign: 'center', marginTop: '10px' }}>
          <button
            className="big-button"
            style={{ fontSize: '0.85em', padding: '6px 12px' }}
            onClick={() => setShowAbcPreview(p => !p)}
          >
            {showAbcPreview ? 'Hide ABC Preview' : 'Show ABC Preview'}
          </button>
        </div>

        {showAbcPreview && (
          <div
            style={{
              marginTop: '12px',
              marginBottom: '20px',
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <div style={{ borderRadius: '8px', backgroundColor: '#fff', padding: '12px' }}>
              <AbcNotation abc={formData.abc || ''} />
            </div>
          </div>
        )}



<textarea
  ref={chordsRef}
  style={inputStyle}
  name="chords"
  placeholder="Chords"
  rows={formData.chords?.trim() ? undefined : 2}
  value={formData.chords || ''}
  onChange={(e) => {
    handleChange(e);
    adjustHeight(chordsRef);
  }}
/>

<textarea
  ref={lyricsRef}
  style={inputStyle}
  name="lyrics"
  placeholder="Lyrics"
  rows={formData.lyrics?.trim() ? undefined : 2}
  value={formData.lyrics || ''}
  onChange={(e) => {
    handleChange(e);
    adjustHeight(lyricsRef);
  }}
/>


<textarea
  ref={notesRef}
  style={inputStyle}
  name="notes"
  placeholder="Notes"
  rows={formData.notes?.trim() ? undefined : 2}
  value={formData.notes || ''}
  onChange={(e) => {
    handleChange(e);
    adjustHeight(notesRef);
  }}
/>

<div style={{ marginTop: '20px', textAlign: 'center' }}>
  <label className="text-scaled">Files:</label>
  <input
    ref={fileInputRef}
    type="file"
    multiple
    onChange={handleFileChange}
    style={{ marginTop: '6px' }}
  />

  <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
    {existingFiles.map((file, i) => (
      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f9f9f9', padding: '6px 10px', borderRadius: '6px' }}>
        <a href={file.url} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>{file.name}</a>
        <button onClick={() => handleDeleteFile(file)} style={{ background: 'transparent', color: '#cc0000', border: 'none', cursor: 'pointer' }}>🗑</button>
      </div>
    ))}
  </div>
</div>


        <input style={inputStyle} name="link1" placeholder="e.g. link to Dropbox, iCloud, or Google Drive recording" value={formData.link1 || ''} onChange={handleChange} />
        <input style={inputStyle} name="link2" placeholder="e.g. link to Spotify, YouTube, or other performance" value={formData.link2 || ''} onChange={handleChange} />
        <input style={inputStyle} name="link3" placeholder="e.g. link to TheSession or other tune database" value={formData.link3 || ''} onChange={handleChange} />

       


{importedTune && (
<div className="import-preview-card" style={{ marginTop: '20px', ...cardStyle }}>


    <h3 className="text-scaled">Review</h3>

    {/* Save and exit buttons */}
    <div style={{
      display: 'flex',
      gap: '12px',
      justifyContent: 'center',
      marginTop: '20px',
      marginBottom: '10px',
      flexWrap: 'wrap'
    }}>
      <button className="big-button" onClick={applyImportedFields}>
        Save Imported Tune
      </button>
      <button
        className="big-button"
        style={{ backgroundColor: '#444', color: '#fff' }}
        onClick={() => {
          setImportedTune(null);
          setImportFields({ title: true, type: true, key: true, abc: true });
          setCustomAbcOverride('');
        }}
      >
        Exit
      </button>
    </div>

    {/* Checkboxes */}
    {Object.keys(importFields).map(field => (
      <label key={field} className="text-scaled" style={{ display: 'block', marginBottom: '8px' }}>
        <input
          type="checkbox"
          checked={importFields[field]}
          onChange={(e) => setImportFields(prev => ({ ...prev, [field]: e.target.checked }))}
        />
        <strong> {field.charAt(0).toUpperCase() + field.slice(1)}:</strong>{' '}
        <span style={{ color: '#ccc' }}>
          {field === 'abc'
            ? (customAbcOverride?.trim() ? 'Preview and save' : '(none)')
            : field === 'key'
              ? normalizeKey(importedTune.key || '')
              : field === 'type'
                ? normalizeType(importedTune.type || '')
                : importedTune[field] || '(none)'}
        </span>

      </label>
    ))}

    {/* ABC preview (only if checked) */}
    <AbcImportPreview
      importedTune={importedTune}
      setImportedTune={setImportedTune}
      importFields={importFields}
      customAbcOverride={customAbcOverride}
      setCustomAbcOverride={setCustomAbcOverride}
      originalAbc={originalAbc}
      setOriginalAbc={setOriginalAbc}
    />
  </div>
)}


  
        {sessionSuggestions.length > 0 && (
          <div>
            <strong className="text-scaled">Matches from TheSession:</strong>
            <ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
              {sessionSuggestions.map((t, i) => (
                <li key={i}><button onClick={() => importFromSession(t.id)}>{t.name} ({t.type})</button></li>
              ))}
            </ul>
          </div>
        )}

        
        {sessionSearchAttempted && sessionSearchLoading && (
          <p className="text-subtle" style={{ textAlign: 'center', marginTop: '12px' }}>
            Searching TheSession.org...
          </p>
        )}

        {sessionSearchAttempted && !sessionSearchLoading && sessionSuggestions.length === 0 && (
          <p className="text-subtle" style={{ textAlign: 'center', marginTop: '12px' }}>
            No matches found on TheSession.org for “{formData?.title}”
          </p>
        )}





  
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', marginTop: '20px' }}>
      <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
        <div style={{ textAlign: 'center' }}>
    <button className="big-button" onClick={fetchSessionSuggestions}>
      Search or Update from thesession.org
    </button>
    <p className="text-subtle" style={{ fontSize: "0.85em", marginTop: "4px" }}>
      import directly by choosing from<br />pop-up matches above
    </p>
    </div>
    <div style={{ textAlign: 'center' }}>
      <button className="big-button" onClick={openTunearchSearch}>
        Search tunearch.org fiddle tune archive </button>
      <p className="text-subtle" style={{ fontSize: "0.85em", marginTop: "4px" }}>
        copy full ABC and paste above<br />(select desktop mode on mobile)</p>
    </div>
    <div style={{ textAlign: 'center' }}>
      <button className="big-button" onClick={openLyricsSearch}>
        Search bluegrasslyrics.com</button>
      <p className="text-subtle" style={{ fontSize: "0.85em", marginTop: "4px" }}>
        copy lyrics and paste above</p>
    </div>
  </div>

  <button className="big-button" onClick={handleSave}
 
    style={{
      background: '#ccc',
      color: '#000',
      padding: '10px 20px',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '1rem'
    }}
     >💾 Save Changes</button>
</div>

      </div>

      <ConfirmLeaveModal
  open={showLeaveModal}
  onCancel={() => setShowLeaveModal(false)}
  onConfirm={() => {
    setShowLeaveModal(false);
    setIsDirty(false);
    if (pendingLeaveAction) pendingLeaveAction();
  }}
/>
    </PageWrapperAuto>
  );
}
